// 환생 렌더(레인 R, REBIRTH-MODES-SPEC 2.1·2.2): 마을① 광장의 윤회의 문(돌 아치 + 빛나는 고리 + 이름표)과 환생 영웅 발밑 오라 고리.
// 최소 연출: 메시 5개 + 이름표 1개, 오라는 가산 고리 1개(드로우콜 +1). 프레임마다 위치·회전·불투명도 값만 바꾼다(할당 없음).
// 날개 외형(1차 환생부터, 스펙 2.2)은 모델이 없어 아직 그리지 않는다 — TODO(레인 A/에셋): Tripo 날개 GLB → 카엘 리그 등 뼈에 붙이기.
import * as THREE from 'three/webgpu';
import { REBIRTH_GATE, rebirthLook } from '../shared/rebirth';
import { Scene, combatScene, type GameView } from '../shared/types';
import { labelSprite } from './env/label';

export class RebirthFx {
  readonly group = new THREE.Group();
  private readonly gate = new THREE.Group();
  private readonly glow: THREE.Mesh;
  private readonly glowMat: THREE.MeshBasicNodeMaterial;
  private readonly ringMat: THREE.MeshBasicNodeMaterial;
  private readonly ring: THREE.Mesh;
  private readonly aura: THREE.Mesh;
  private readonly auraMat: THREE.MeshBasicNodeMaterial;
  private auraCycle = -1;

  constructor() {
    this.group.name = 'rebirth-fx';
    const stone = new THREE.MeshStandardNodeMaterial({ color: 0xe7dcc4, roughness: 0.8 });
    const gold = new THREE.MeshStandardNodeMaterial({ color: 0xd8b25a, roughness: 0.35, metalness: 0.7, emissive: 0x4a3410 });
    // 아치: 두 기둥 + 위 반원(동쪽을 향해 선다). 키 4.6m
    const pillarGeo = new THREE.BoxGeometry(0.55, 3.2, 0.55);
    for (const s of [-1, 1]) {
      const p = new THREE.Mesh(pillarGeo, stone);
      p.position.set(0, 1.6, s * 1.75);
      p.castShadow = true;
      this.gate.add(p);
    }
    const arch = new THREE.Mesh(new THREE.TorusGeometry(1.75, 0.28, 8, 24, Math.PI), stone);
    arch.position.y = 3.2; arch.rotation.y = Math.PI / 2;
    this.gate.add(arch);
    this.ringMat = new THREE.MeshBasicNodeMaterial({ color: 0xffe7a0, transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending });
    this.ring = new THREE.Mesh(new THREE.TorusGeometry(1.35, 0.07, 6, 40), this.ringMat);
    this.ring.position.y = 2.25; this.ring.rotation.y = Math.PI / 2;
    this.gate.add(this.ring);
    this.glowMat = new THREE.MeshBasicNodeMaterial({ color: 0xfff1c8, transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.glow = new THREE.Mesh(new THREE.CircleGeometry(1.3, 32), this.glowMat);
    this.glow.position.y = 2.25; this.glow.rotation.y = Math.PI / 2;
    this.gate.add(this.glow);
    const step = new THREE.Mesh(new THREE.CylinderGeometry(2.3, 2.5, 0.18, 24, 1, false, 0, Math.PI), gold);
    step.position.y = 0.09; step.rotation.y = 0;
    this.gate.add(step);
    const label = labelSprite('윤회의 문', '#ffe08a', 3.2, 'facility');
    label.position.set(0.2, 5.0, 0);
    this.gate.add(label);
    this.gate.position.set(REBIRTH_GATE.archX, 0, REBIRTH_GATE.archZ);
    this.gate.visible = false;
    this.group.add(this.gate);
    // 오라: 발밑 가는 고리(회차 색). 영웅 위치를 따라간다
    this.auraMat = new THREE.MeshBasicNodeMaterial({ color: 0x3f6fe0, transparent: true, opacity: 0.7, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.aura = new THREE.Mesh(new THREE.RingGeometry(0.62, 0.78, 40), this.auraMat);
    this.aura.rotation.x = -Math.PI / 2;
    this.aura.renderOrder = 3;
    this.aura.visible = false;
    this.group.add(this.aura);
  }

  /** 프레임마다: 문 보이기·맥동, 오라 위치·색. timeSec = 실시간(초) */
  update(view: GameView, hx: number, hz: number, timeSec: number, reduceFlash: boolean): void {
    const r = view.rebirth;
    const gateOn = !!r && r.gateVisible && view.map.id === 'village';
    this.gate.visible = gateOn;
    if (gateOn) {
      const k = reduceFlash ? 0.5 : 1;
      this.glowMat.opacity = (0.42 + 0.12 * Math.sin(timeSec * 1.7)) * k + 0.1;
      this.ring.rotation.x = timeSec * 0.4;
      this.ringMat.opacity = 0.75 + 0.15 * Math.sin(timeSec * 2.3);
    }
    const cyc = r ? r.cycle : 0;
    const sc = view.flow.scene;
    const auraOn = cyc > 0 && (sc === Scene.Village || combatScene(sc));
    this.aura.visible = auraOn;
    if (!auraOn) return;
    if (cyc !== this.auraCycle) { this.auraCycle = cyc; this.auraMat.color.setHex(rebirthLook(cyc).aura); }
    this.aura.position.set(hx, 0.04, hz);
    this.aura.rotation.z = timeSec * 0.6;
    const s = 1 + 0.06 * Math.sin(timeSec * 2);
    this.aura.scale.set(s, s, s);
    this.auraMat.opacity = reduceFlash ? 0.45 : 0.55 + 0.15 * Math.sin(timeSec * 2);
  }
}
